import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { getColors, typography, spacing } from '../tokens';
import { StarsIcon } from '../icons/StarsIcon';
import { PrimaryGradientDefs } from './PrimaryGradientDefs';

const GRADIENT_ID = 'journey-card-icon-gradient';
const BG_GRADIENT_ID = 'journey-card-bg-gradient';

export interface JourneyCardProps {
  title: string;
  subtitle?: string;
  description?: string;
  tags?: string[];
  icon?: ReactNode;
  href?: string;
  featured?: boolean;
  onClick?: () => void;
}

const useStyles = makeStyles<{ featured: boolean }>()((theme, { featured }) => {
  const c = getColors(theme.palette.mode);
  const isLight = theme.palette.mode === 'light';
  const fgAlpha = (a: number) =>
    isLight ? `rgba(0, 0, 0, ${a})` : `rgba(255, 255, 255, ${a})`;
  return {
    root: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
      padding: featured
        ? `${spacing(3)}px ${spacing(3.5)}px`
        : `${spacing(2)}px ${spacing(2.5)}px`,
      borderRadius: 12,
      backgroundColor: c.background.paper,
      border: `1px solid ${c.border.default}`,
      cursor: 'pointer',
      transition: 'border-color 0.2s ease, background-color 0.2s ease',
      position: 'relative',
      overflow: 'hidden',
      height: '100%',
      minHeight: featured ? 180 : 'auto',
      '&:hover': {
        borderColor: 'rgba(0, 120, 214, 0.4)',
        backgroundColor: c.background.elevated,
      },
    },
    featuredBackground: {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      width: '50%',
      opacity: 0.06,
      background:
        'radial-gradient(circle at 80% 50%, rgba(0, 120, 214, 0.8) 0%, transparent 70%)',
      pointerEvents: 'none',
    },
    featuredBgStar: {
      position: 'absolute',
      top: '50%',
      right: spacing(3),
      transform: 'translateY(-22%) translateX(-50%) scaleX(-1)',
      pointerEvents: 'none',
      '& svg': {
        width: 240,
        height: 240,
        '& path': {
          fill: `url(#${BG_GRADIENT_ID})`,
        },
      },
    },
    header: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 12,
      marginBottom: 8,
      position: 'relative',
      zIndex: 1,
    },
    iconWrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      '& svg': {
        width: featured ? 36 : 18,
        height: featured ? 36 : 18,
        '& path': {
          fill: `url(#${GRADIENT_ID})`,
        },
      },
    },
    titleGroup: {
      flex: 1,
      minWidth: 0,
    },
    title: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: featured ? 32 : 14,
      fontWeight: 700,
      color: c.text.primary,
      lineHeight: featured ? '37px' : '20px',
      margin: 0,
    },
    subtitle: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: 12,
      fontWeight: 400,
      color: c.text.secondary,
      marginTop: 2,
      lineHeight: 1.3,
    },
    description: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: 13,
      fontWeight: 400,
      color: c.text.secondary,
      lineHeight: 1.5,
      marginTop: 8,
      position: 'relative',
      zIndex: 1,
      display: '-webkit-box',
      WebkitLineClamp: featured ? 4 : 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    tags: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 6,
      marginTop: 12,
      position: 'relative',
      zIndex: 1,
    },
    tag: {
      display: 'inline-block',
      padding: 0,
      color: c.text.primary,
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: 11,
      fontWeight: 400,
      lineHeight: 1.3,
      whiteSpace: 'nowrap' as const,
    },
    arrow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      marginTop: 'auto',
      paddingTop: 12,
      position: 'relative',
      zIndex: 1,
      color: fgAlpha(0.4),
      fontSize: 18,
      '& svg': {
        width: 18,
        height: 18,
      },
    },
  };
});

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z" />
  </svg>
);

export const JourneyCard = ({
  title,
  subtitle,
  description,
  tags,
  icon,
  href,
  featured = false,
  onClick,
}: Readonly<JourneyCardProps>) => {
  const { classes } = useStyles({ featured });
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (href) {
      navigate(href);
    }
  };

  const isInteractive = Boolean(href || onClick);

  const Root = isInteractive ? 'button' : 'div';

  return (
    <Root
      type={isInteractive ? 'button' : undefined}
      className={classes.root}
      onClick={isInteractive ? handleClick : undefined}
    >
      <PrimaryGradientDefs id={GRADIENT_ID} />
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <linearGradient
            id={BG_GRADIENT_ID}
            x1="0%"
            y1="0%"
            x2="100%"
            y2="100%"
          >
            <stop offset="0%" stopColor="#FFFFFF" stopOpacity={0} />
            <stop offset="80%" stopColor="#FFFFFF" stopOpacity={0.34} />
            <stop offset="84.3%" stopColor="#7C7C7C" stopOpacity={0.69} />
            <stop offset="100%" stopColor="#262626" stopOpacity={0.86} />
          </linearGradient>
        </defs>
      </svg>
      {featured && <div className={classes.featuredBackground} />}
      {featured && (
        <div className={classes.featuredBgStar}>
          <StarsIcon />
        </div>
      )}
      <div className={classes.header}>
        {icon && <div className={classes.iconWrapper}>{icon}</div>}
        <div className={classes.titleGroup}>
          <h3 className={classes.title}>{title}</h3>
          {subtitle && <div className={classes.subtitle}>{subtitle}</div>}
        </div>
      </div>
      {description && <p className={classes.description}>{description}</p>}
      {tags && tags.length > 0 && (
        <div className={classes.tags}>
          {tags.map(tag => (
            <span key={tag} className={classes.tag}>
              {tag}
            </span>
          ))}
        </div>
      )}
      {isInteractive && (
        <div className={classes.arrow}>
          <ArrowIcon />
        </div>
      )}
    </Root>
  );
};
